'use client';

import { useQuery } from '@/lib/convex-react';
import {
	ArrowLeft,
	Calendar,
	ChevronRight,
	Clock,
	Facebook,
	Link2,
	Linkedin,
	List,
	Menu,
	Sparkles,
	Twitter,
	User,
	X,
} from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import BlogCard from '@/components/landing/BlogCard';
import SiteFooter from '@/components/landing/SiteFooter';
import NewsletterSignup from '@/components/newsletter/NewsletterSignup';
import Image from '@/components/ui/CspImage';
import WaitlistModal from '@/components/WaitlistModal';
import { api } from '@/convex/_generated/api';
import { Link } from '@/i18n/navigation';
import { rehypeStripUnsafe } from '@/lib/sanitize-hast';

interface BlogPostClientProps {
	slug: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	initialPost?: Record<string, any> | null;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	initialRelatedPosts?: Record<string, any>[];
}

// Category color mapping - using landing page primary color
const categoryColors: Record<string, { bg: string; text: string }> = {
	'fashion-trends': { bg: 'bg-primary/10', text: 'text-primary' },
	'product-updates': { bg: 'bg-blue-500/10', text: 'text-blue-600' },
	tutorials: { bg: 'bg-emerald-500/10', text: 'text-emerald-600' },
	guides: { bg: 'bg-emerald-500/10', text: 'text-emerald-600' },
	'industry-insights': { bg: 'bg-blue-500/10', text: 'text-blue-600' },
	'company-news': { bg: 'bg-violet-500/10', text: 'text-violet-600' },
	'ai-insights': { bg: 'bg-amber-500/10', text: 'text-amber-600' },
};

export default function BlogPostClient({
	slug,
	initialPost,
	initialRelatedPosts,
}: BlogPostClientProps) {
	const t = useTranslations('blog');
	const tNav = useTranslations('nav');
	const tCta = useTranslations('cta');
	const locale = useLocale();
	const isZh = locale === 'zh-TW';
	const postLive = useQuery(api.blogPosts.getPostBySlug, { slug });
	// Use live data when available, fall back to server-fetched initial data for SSR
	const post = postLive !== undefined ? postLive : initialPost;
	const relatedPostsLive = useQuery(
		api.blogPosts.getRelatedPosts,
		post
			? { slug: post.slug, categorySlug: post.categorySlug, limit: 3 }
			: 'skip',
	);
	const relatedPosts = relatedPostsLive ?? initialRelatedPosts;
	const waitlistMode =
		useQuery(api.systemSettings.getWaitlistMode, {}) ?? false;
	const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [readingProgress, setReadingProgress] = useState(0);
	const [activeHeadingId, setActiveHeadingId] = useState<string>('');
	const [isTocOpen, setIsTocOpen] = useState(false);
	const articleRef = useRef<HTMLDivElement>(null);

	// Extract TOC headings from markdown content
	const tocHeadings = useMemo(() => {
		const content = isZh && post?.contentZh ? post.contentZh : post?.content;
		if (!content) return [];
		const headingRegex = /^(#{2,3})\s+(.+)$/gm;
		const headings: { level: number; text: string; id: string }[] = [];
		let match: RegExpExecArray | null;
		// biome-ignore lint/suspicious/noAssignInExpressions: idiomatic global-regex exec loop; splitting the assignment would duplicate the exec call
		while ((match = headingRegex.exec(content)) !== null) {
			const text = match[2].replace(/\*\*/g, '').replace(/\*/g, '').trim();
			const id = text
				.toLowerCase()
				.replace(/[^\w\s\u4e00-\u9fff\u3400-\u4dbf-]/g, '')
				.replace(/\s+/g, '-')
				.replace(/-+/g, '-');
			headings.push({ level: match[1].length, text, id });
		}
		return headings;
	}, [post, isZh]);

	// Reading progress bar
	useEffect(() => {
		const handleScroll = () => {
			const article = articleRef.current;
			if (!article) return;
			const rect = article.getBoundingClientRect();
			const articleTop = rect.top + window.scrollY;
			const articleHeight = rect.height;
			const scrolled = window.scrollY - articleTop;
			const progress = Math.min(
				100,
				Math.max(0, (scrolled / (articleHeight - window.innerHeight)) * 100),
			);
			setReadingProgress(progress);
		};
		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	// Active heading tracking via IntersectionObserver
	useEffect(() => {
		if (tocHeadings.length === 0) return;
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setActiveHeadingId(entry.target.id);
					}
				}
			},
			{ rootMargin: '-80px 0px -70% 0px', threshold: 0 },
		);
		const headingElements = document.querySelectorAll(
			'.blog-prose h2[id], .blog-prose h3[id]',
		);
		headingElements.forEach((el) => {
			observer.observe(el);
		});
		return () => observer.disconnect();
	}, [tocHeadings]);

	const scrollToHeading = useCallback((id: string) => {
		const el = document.getElementById(id);
		if (el) {
			const offset = 100;
			const top = el.getBoundingClientRect().top + window.scrollY - offset;
			window.scrollTo({ top, behavior: 'smooth' });
			setIsTocOpen(false);
		}
	}, []);

	const handleShare = (platform: string) => {
		const url = window.location.href;
		const title = post?.title || 'Check out this article';

		switch (platform) {
			case 'twitter':
				window.open(
					`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
					'_blank',
				);
				break;
			case 'linkedin':
				window.open(
					`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
					'_blank',
				);
				break;
			case 'facebook':
				window.open(
					`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
					'_blank',
				);
				break;
			case 'copy':
				navigator.clipboard.writeText(url);
				alert('Link copied to clipboard!');
				break;
		}
	};

	// Only show loading if neither live nor initial data is available
	if (post === undefined) {
		return (
			<div className="landing-theme flex min-h-screen items-center justify-center bg-background font-body">
				<div className="animate-pulse text-muted-foreground">
					{t('loading')}
				</div>
			</div>
		);
	}

	if (post === null) {
		return (
			<div className="landing-theme min-h-screen bg-background font-body">
				<nav
					aria-label="Main navigation"
					className="fixed top-0 z-50 w-full border-border border-b bg-background/95 backdrop-blur-md"
				>
					<div className="mx-auto max-w-7xl px-6 py-4">
						<Link
							href="/"
							className="flex items-center"
						>
							<Image
								src="/StyTrix_brand.png"
								alt="StyTrix"
								width={140}
								height={37}
								className="block h-[2.25rem] w-auto dark:hidden"
								priority
							/>
							<Image
								src="/StyTrix_brand_dark.png"
								alt="StyTrix"
								width={140}
								height={37}
								className="hidden h-[2.25rem] w-auto dark:block"
								priority
							/>
						</Link>
					</div>
				</nav>
				<main
					id="main-content"
					className="px-6 pt-32 pb-16 text-center"
				>
					<h1 className="mb-4 font-bold text-4xl text-foreground tracking-tight">
						{t('notFoundTitle')}
					</h1>
					<p className="mb-8 text-muted-foreground">
						{t('notFoundDescription')}
					</p>
					<Link
						href="/blog"
						className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white transition-all duration-300 hover:bg-primary/90 hover:shadow-lg"
					>
						<ArrowLeft className="h-4 w-4" />
						{t('backToBlog')}
					</Link>
				</main>
			</div>
		);
	}

	const colors =
		categoryColors[post.categorySlug] || categoryColors['fashion-trends'];
	const publishedDate = post.publishedAt
		? new Date(post.publishedAt)
		: new Date(post.createdAt);

	return (
		<div className="landing-theme min-h-screen overflow-x-hidden bg-background font-body">
			{/* Navigation - matching landing page */}
			<nav
				aria-label="Main navigation"
				className="fixed top-0 z-50 w-full border-border border-b bg-background/95 backdrop-blur-md"
			>
				<div className="mx-auto max-w-7xl px-6 py-4">
					<div className="flex items-center justify-between">
						<Link
							href="/"
							className="flex items-center"
						>
							<Image
								src="/StyTrix_brand.png"
								alt="StyTrix"
								width={140}
								height={37}
								className="block h-[2.25rem] w-auto dark:hidden"
								priority
							/>
							<Image
								src="/StyTrix_brand_dark.png"
								alt="StyTrix"
								width={140}
								height={37}
								className="hidden h-[2.25rem] w-auto dark:block"
								priority
							/>
						</Link>

						{/* Navigation Links - matching landing page */}
						<div className="hidden items-center gap-8 md:flex">
							<Link
								href="/documentation"
								className="font-medium text-muted-foreground text-sm transition-colors hover:text-foreground"
							>
								{tNav('documentation')}
							</Link>
							<Link
								href="/#pricing"
								className="font-medium text-muted-foreground text-sm transition-colors hover:text-foreground"
							>
								{tNav('pricing')}
							</Link>
							<Link
								href="/blog"
								className="font-medium text-foreground text-sm transition-colors"
							>
								{tNav('blog')}
							</Link>
						</div>

						<div className="flex items-center gap-3">
							<LanguageSwitcher className="hidden md:flex" />
							{waitlistMode ? (
								<button
									onClick={() => setIsWaitlistOpen(true)}
									className="hidden whitespace-nowrap rounded-lg bg-primary px-6 py-2.5 font-medium text-white transition-all duration-300 hover:bg-primary/90 hover:shadow-lg sm:block"
								>
									{tNav('joinWaitlist')}
								</button>
							) : (
								<a
									href="/sign-in"
									className="hidden rounded-lg bg-primary px-6 py-2.5 font-medium text-white transition-all duration-300 hover:bg-primary/90 hover:shadow-lg sm:block"
								>
									{tNav('signIn')}
								</a>
							)}

							{/* Mobile Menu Button */}
							<button
								onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
								className="rounded-lg p-2.5 transition-colors hover:bg-secondary md:hidden"
								aria-label="Toggle menu"
								aria-expanded={isMobileMenuOpen}
							>
								{isMobileMenuOpen ? (
									<X className="h-6 w-6" />
								) : (
									<Menu className="h-6 w-6" />
								)}
							</button>
						</div>
					</div>
				</div>

				{/* Mobile Menu */}
				<div
					className={`absolute top-full right-0 left-0 border-border border-b bg-background/95 shadow-elevated-lg backdrop-blur-lg transition-all duration-300 md:hidden ${
						isMobileMenuOpen
							? 'translate-y-0 opacity-100'
							: 'pointer-events-none -translate-y-4 opacity-0'
					}`}
				>
					<div className="space-y-4 px-6 py-6">
						<Link
							href="/documentation"
							onClick={() => setIsMobileMenuOpen(false)}
							className="block border-border border-b py-3 font-medium text-foreground text-lg transition-colors hover:text-primary"
						>
							{tNav('documentation')}
						</Link>
						<Link
							href="/#pricing"
							onClick={() => setIsMobileMenuOpen(false)}
							className="block border-border border-b py-3 font-medium text-foreground text-lg transition-colors hover:text-primary"
						>
							{tNav('pricing')}
						</Link>
						<Link
							href="/blog"
							onClick={() => setIsMobileMenuOpen(false)}
							className="block border-border border-b py-3 font-medium text-foreground text-lg transition-colors hover:text-primary"
						>
							{tNav('blog')}
						</Link>
						{waitlistMode ? (
							<button
								onClick={() => {
									setIsWaitlistOpen(true);
									setIsMobileMenuOpen(false);
								}}
								className="mt-4 w-full rounded-lg bg-primary px-6 py-3 font-medium text-white transition-all hover:bg-primary/90"
							>
								{tNav('joinWaitlist')}
							</button>
						) : (
							<a
								href="/sign-in"
								className="mt-4 block w-full rounded-lg bg-primary px-6 py-3 text-center font-medium text-white transition-all hover:bg-primary/90"
							>
								{tNav('signIn')}
							</a>
						)}
						<div className="flex items-center justify-center border-border border-t pt-4">
							<LanguageSwitcher />
						</div>
					</div>
				</div>
			</nav>
			<main id="main-content">
				{/* Reading Progress Bar */}
				<div className="fixed top-0 right-0 left-0 z-[60] h-[3px]">
					<div
						className="h-full bg-gradient-to-r from-primary to-primary/70 transition-[width] duration-150 ease-out"
						ref={(element) => {
							if (element) element.style.width = `${readingProgress}%`;
						}}
					/>
				</div>

				{/* Article Header */}
				<header className="bg-gradient-to-b from-secondary to-background px-6 pt-32 pb-8">
					<div className="mx-auto max-w-4xl">
						{/* Breadcrumb */}
						<nav
							aria-label="Breadcrumb"
							className="mb-8 flex items-center gap-2 overflow-hidden text-muted-foreground text-sm"
						>
							<Link
								href="/"
								className="shrink-0 whitespace-nowrap transition-colors hover:text-primary"
							>
								{t('home')}
							</Link>
							<ChevronRight className="h-4 w-4 shrink-0" />
							<Link
								href="/blog"
								className="shrink-0 whitespace-nowrap transition-colors hover:text-primary"
							>
								{tNav('blog')}
							</Link>
							<ChevronRight className="h-4 w-4 shrink-0" />
							<span className="truncate font-medium text-foreground">
								{isZh && post.titleZh ? post.titleZh : post.title}
							</span>
						</nav>

						{/* Meta */}
						<div className="mb-6 flex flex-wrap items-center gap-4">
							<span
								className={`rounded-full px-3 py-1 font-medium text-sm ${colors.bg} ${colors.text}`}
							>
								{post.category?.name || post.categorySlug}
							</span>
							<div className="flex items-center gap-2 text-muted-foreground text-sm">
								<Calendar className="h-4 w-4" />
								<time dateTime={publishedDate.toISOString()}>
									{publishedDate.toLocaleDateString(isZh ? 'zh-TW' : 'en-US', {
										year: 'numeric',
										month: 'long',
										day: 'numeric',
									})}
								</time>
							</div>
							{post.readingTimeMinutes && (
								<div className="flex items-center gap-2 text-muted-foreground text-sm">
									<Clock className="h-4 w-4" />
									{t('minRead', { minutes: post.readingTimeMinutes })}
								</div>
							)}
							{post.aiGenerated && (
								<div className="flex items-center gap-1 text-primary text-sm">
									<Sparkles className="h-4 w-4" />
									{t('aiGenerated')}
								</div>
							)}
						</div>

						{/* Title */}
						<h1 className="mb-6 font-bold text-4xl text-foreground leading-tight tracking-tight md:text-5xl lg:text-6xl">
							{isZh && post.titleZh ? post.titleZh : post.title}
						</h1>

						{/* Excerpt */}
						{(isZh ? post.excerptZh || post.excerpt : post.excerpt) && (
							<p className="mb-8 text-muted-foreground text-xl leading-relaxed">
								{isZh && post.excerptZh ? post.excerptZh : post.excerpt}
							</p>
						)}

						{/* Author & Share */}
						<div className="flex flex-wrap items-center justify-between gap-4 border-border border-b pb-8">
							<div className="flex items-center gap-3">
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 ring-2 ring-transparent transition-all duration-300 hover:ring-primary/20">
									<User className="h-6 w-6 text-primary" />
								</div>
								<div>
									<div className="font-medium text-foreground">
										{post.authorName || 'StyTrix Team'}
									</div>
									<div className="text-muted-foreground text-sm">
										{t('author')}
									</div>
								</div>
							</div>

							<div className="flex items-center gap-2">
								<span className="mr-2 text-muted-foreground text-sm">
									{t('shareArticle')}:
								</span>
								<button
									onClick={() => handleShare('twitter')}
									className="rounded-lg p-2.5 transition-all duration-200 hover:scale-110 hover:bg-card"
									aria-label="Share on Twitter"
								>
									<Twitter className="h-5 w-5 text-muted-foreground transition-colors hover:text-primary" />
								</button>
								<button
									onClick={() => handleShare('linkedin')}
									className="rounded-lg p-2.5 transition-all duration-200 hover:scale-110 hover:bg-card"
									aria-label="Share on LinkedIn"
								>
									<Linkedin className="h-5 w-5 text-muted-foreground transition-colors hover:text-primary" />
								</button>
								<button
									onClick={() => handleShare('facebook')}
									className="rounded-lg p-2.5 transition-all duration-200 hover:scale-110 hover:bg-card"
									aria-label="Share on Facebook"
								>
									<Facebook className="h-5 w-5 text-muted-foreground transition-colors hover:text-primary" />
								</button>
								<button
									onClick={() => handleShare('copy')}
									className="rounded-lg p-2.5 transition-all duration-200 hover:scale-110 hover:bg-card"
									aria-label="Copy link"
								>
									<Link2 className="h-5 w-5 text-muted-foreground transition-colors hover:text-primary" />
								</button>
							</div>
						</div>
					</div>
				</header>

				{/* Featured Image */}
				{(post.r2FeaturedImageUrl || post.featuredImageUrl) && (
					<div className="mb-12 px-6">
						<div className="mx-auto max-w-5xl">
							<div className="relative aspect-[21/9] overflow-hidden rounded-2xl shadow-elevated-lg">
								<Image
									src={post.r2FeaturedImageUrl || post.featuredImageUrl || ''}
									alt={post.featuredImageAlt || post.title}
									fill
									className="object-cover"
									sizes="(max-width: 1200px) 100vw, 1200px"
									priority
								/>
							</div>
						</div>
					</div>
				)}

				{/* Article Content with Sticky Sidebar CTA */}
				<article
					className="px-6 pb-16"
					ref={articleRef}
				>
					<div className="mx-auto max-w-6xl">
						<div className="grid gap-12 lg:grid-cols-[1fr,280px]">
							{/* Main Content */}
							<div className="min-w-0 max-w-3xl">
								{/* Table of Contents */}
								{tocHeadings.length > 2 && (
									<div className="mb-10 overflow-hidden rounded-xl border border-border bg-card/50">
										<button
											onClick={() => setIsTocOpen(!isTocOpen)}
											className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-secondary/50"
										>
											<div className="flex items-center gap-2">
												<List className="h-4 w-4 text-primary" />
												<span className="font-semibold text-foreground text-sm">
													{isZh ? '目錄' : 'Table of Contents'}
												</span>
												<span className="text-muted-foreground text-xs">
													({tocHeadings.filter((h) => h.level === 2).length}{' '}
													{isZh ? '章節' : 'sections'})
												</span>
											</div>
											<ChevronRight
												className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isTocOpen ? 'rotate-90' : ''}`}
											/>
										</button>
										<div
											className={`transition-all duration-300 ease-in-out ${isTocOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'} overflow-y-auto`}
										>
											<nav
												aria-label="On this page"
												className="ml-6 space-y-1 border-primary/20 border-l-[3px] px-6 pb-4"
											>
												{tocHeadings.map((heading) => (
													<button
														key={heading.id}
														onClick={() => scrollToHeading(heading.id)}
														className={`block w-full py-1.5 text-left text-sm transition-colors duration-200 ${
															heading.level === 3 ? 'pl-4' : 'pl-0 font-medium'
														} ${
															activeHeadingId === heading.id
																? 'text-primary'
																: 'text-muted-foreground hover:text-foreground'
														}`}
													>
														{heading.text}
													</button>
												))}
											</nav>
										</div>
									</div>
								)}

								<div className="blog-prose prose prose-lg dark:prose-invert prose-h2:mt-14 prose-h3:mt-10 prose-h2:mb-5 prose-h3:mb-4 prose-p:mb-6 max-w-none prose-pre:max-w-full prose-headings:scroll-mt-24 prose-pre:overflow-x-auto break-words prose-code:break-words prose-code:rounded prose-img:rounded-xl prose-pre:rounded-xl prose-blockquote:rounded-r-lg prose-pre:border prose-a:border-primary/30 prose-h2:border-border/50 prose-hr:border-border/50 prose-pre:border-border prose-a:border-b prose-h2:border-b prose-blockquote:border-l-primary/40 prose-blockquote:bg-primary/5 prose-code:bg-primary/5 prose-pre:bg-card prose-blockquote:px-1 prose-code:px-1.5 prose-blockquote:py-1 prose-code:py-0.5 prose-h2:pb-3 prose-headings:font-display prose-strong:font-semibold prose-a:text-primary prose-code:text-primary prose-code:text-sm prose-h2:text-2xl prose-h3:text-xl prose-headings:text-foreground prose-li:text-[17px] prose-li:text-muted-foreground prose-p:text-[17px] prose-p:text-muted-foreground prose-strong:text-foreground prose-li:leading-[1.85] prose-p:leading-[1.85] prose-a:no-underline prose-img:shadow-lg prose-a:transition-colors prose-ol:marker:font-semibold prose-li:marker:text-primary/60 prose-ol:marker:text-primary/60 hover:prose-a:border-primary prose-h2:md:text-3xl prose-h3:md:text-2xl">
									<ReactMarkdown
										remarkPlugins={[remarkGfm]}
										rehypePlugins={[rehypeRaw, rehypeStripUnsafe]}
										components={{
											h2: ({ children, ...props }) => {
												const text = String(children)
													.replace(/\*\*/g, '')
													.replace(/\*/g, '');
												const id = text
													.toLowerCase()
													.replace(/[^\w\s\u4e00-\u9fff\u3400-\u4dbf-]/g, '')
													.replace(/\s+/g, '-')
													.replace(/-+/g, '-');
												return (
													<h2
														id={id}
														{...props}
													>
														{children}
													</h2>
												);
											},
											h3: ({ children, ...props }) => {
												const text = String(children)
													.replace(/\*\*/g, '')
													.replace(/\*/g, '');
												const id = text
													.toLowerCase()
													.replace(/[^\w\s\u4e00-\u9fff\u3400-\u4dbf-]/g, '')
													.replace(/\s+/g, '-')
													.replace(/-+/g, '-');
												return (
													<h3
														id={id}
														{...props}
													>
														{children}
													</h3>
												);
											},
										}}
									>
										{isZh && post.contentZh ? post.contentZh : post.content}
									</ReactMarkdown>
								</div>

								{/* Tags */}
								{post.tags && post.tags.length > 0 && (
									<div className="mt-12 border-border border-t pt-8">
										<div className="flex flex-wrap gap-2">
											{post.tags.map((tag: string, index: number) => (
												<span
													// biome-ignore lint/suspicious/noArrayIndexKey: tag list comes straight from the post record and is never reordered; tags are not guaranteed unique
													key={index}
													className="rounded-full border border-border bg-card px-3 py-1 text-muted-foreground text-sm transition-colors duration-200 hover:border-primary/30 hover:text-foreground"
												>
													#{tag}
												</span>
											))}
										</div>
									</div>
								)}
							</div>

							{/* Sticky Sidebar - Desktop Only */}
							<aside className="hidden lg:block">
								<div className="sticky top-28 space-y-6">
									{/* Sidebar TOC */}
									{tocHeadings.length > 2 && (
										<nav
											aria-label="On this page"
											className="rounded-xl border border-border bg-card/50 p-4"
										>
											<p className="mb-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">
												{isZh ? '本文目錄' : 'On this page'}
											</p>
											<div className="max-h-[300px] space-y-0.5 overflow-y-auto">
												{tocHeadings
													.filter((h) => h.level === 2)
													.map((heading) => (
														<button
															key={heading.id}
															onClick={() => scrollToHeading(heading.id)}
															className={`block w-full truncate py-1.5 pr-2 text-left text-xs transition-colors duration-200 ${
																activeHeadingId === heading.id
																	? 'font-medium text-primary'
																	: 'text-muted-foreground hover:text-foreground'
															}`}
														>
															{heading.text}
														</button>
													))}
											</div>
										</nav>
									)}
									<div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 shadow-elevated">
										<div className="mb-4 flex items-center gap-2">
											<Sparkles className="h-5 w-5 text-primary" />
											<span className="font-medium text-primary text-sm">
												{t('sidebarCtaBadge')}
											</span>
										</div>
										<h3 className="mb-2 font-bold text-foreground text-lg tracking-tight">
											{t('sidebarCtaTitle')}
										</h3>
										<p className="mb-4 text-muted-foreground text-sm">
											{t('sidebarCtaDescription')}
										</p>
										{waitlistMode ? (
											<button
												onClick={() => setIsWaitlistOpen(true)}
												className="block w-full rounded-lg bg-primary px-4 py-3 text-center font-medium text-white transition-all duration-300 hover:bg-primary/90 hover:shadow-lg"
											>
												{t('sidebarCtaButtonWaitlist')}
											</button>
										) : (
											<a
												href="/sign-in"
												className="block w-full rounded-lg bg-primary px-4 py-3 text-center font-medium text-white transition-all duration-300 hover:bg-primary/90 hover:shadow-lg"
											>
												{t('sidebarCtaButton')}
											</a>
										)}
										<p className="mt-3 text-center text-muted-foreground text-xs">
											{waitlistMode
												? t('sidebarCtaNoteWaitlist')
												: t('sidebarCtaNote')}
										</p>
									</div>

									{/* Social Share - Sidebar */}
									<div className="rounded-xl border border-border bg-card p-4">
										<p className="mb-3 font-medium text-foreground text-sm">
											{t('shareArticle')}
										</p>
										<div className="flex items-center gap-2">
											<button
												onClick={() => handleShare('twitter')}
												className="flex flex-1 items-center justify-center rounded-lg p-2 transition-all duration-200 hover:scale-110 hover:bg-secondary"
												aria-label="Share on Twitter"
											>
												<Twitter className="h-4 w-4 text-muted-foreground hover:text-primary" />
											</button>
											<button
												onClick={() => handleShare('linkedin')}
												className="flex flex-1 items-center justify-center rounded-lg p-2 transition-all duration-200 hover:scale-110 hover:bg-secondary"
												aria-label="Share on LinkedIn"
											>
												<Linkedin className="h-4 w-4 text-muted-foreground hover:text-primary" />
											</button>
											<button
												onClick={() => handleShare('facebook')}
												className="flex flex-1 items-center justify-center rounded-lg p-2 transition-all duration-200 hover:scale-110 hover:bg-secondary"
												aria-label="Share on Facebook"
											>
												<Facebook className="h-4 w-4 text-muted-foreground hover:text-primary" />
											</button>
											<button
												onClick={() => handleShare('copy')}
												className="flex flex-1 items-center justify-center rounded-lg p-2 transition-all duration-200 hover:scale-110 hover:bg-secondary"
												aria-label="Copy link"
											>
												<Link2 className="h-4 w-4 text-muted-foreground hover:text-primary" />
											</button>
										</div>
									</div>

									{/* Newsletter Signup */}
									<NewsletterSignup
										variant="sidebar"
										source="blog-post"
									/>
								</div>
							</aside>
						</div>

						{/* Mobile CTA - Shows only on mobile */}
						<div className="mt-12 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 lg:hidden">
							<div className="mb-3 flex items-center gap-2">
								<Sparkles className="h-5 w-5 text-primary" />
								<span className="font-medium text-primary text-sm">
									{t('sidebarCtaBadge')}
								</span>
							</div>
							<h3 className="mb-2 font-bold text-foreground text-lg tracking-tight">
								{t('sidebarCtaTitleMobile')}
							</h3>
							<p className="mb-4 text-muted-foreground text-sm">
								{t('sidebarCtaDescription')}
							</p>
							{waitlistMode ? (
								<button
									onClick={() => setIsWaitlistOpen(true)}
									className="block w-full rounded-lg bg-primary px-4 py-3 text-center font-medium text-white transition-all duration-300 hover:bg-primary/90 hover:shadow-lg"
								>
									{t('sidebarCtaButtonWaitlist')}
								</button>
							) : (
								<a
									href="/sign-in"
									className="block w-full rounded-lg bg-primary px-4 py-3 text-center font-medium text-white transition-all duration-300 hover:bg-primary/90 hover:shadow-lg"
								>
									{t('sidebarCtaButton')}
								</a>
							)}
						</div>
					</div>
				</article>

				{/* Related Posts */}
				{relatedPosts && relatedPosts.length > 0 && (
					<section className="border-border border-t bg-secondary px-6 py-20">
						<div className="mx-auto max-w-7xl">
							<h2 className="mb-12 text-center font-bold text-3xl text-foreground tracking-tight md:text-4xl">
								{t('relatedArticles')}
							</h2>
							<div className="grid gap-8 md:grid-cols-3">
								{relatedPosts.map((relatedPost) => (
									<BlogCard
										key={relatedPost._id}
										title={relatedPost.title}
										titleZh={relatedPost.titleZh}
										slug={relatedPost.slug}
										excerpt={relatedPost.excerpt}
										excerptZh={relatedPost.excerptZh}
										featuredImageUrl={
											relatedPost.r2FeaturedImageUrl ||
											relatedPost.featuredImageUrl
										}
										featuredImageAlt={relatedPost.featuredImageAlt}
										categoryName={
											relatedPost.category?.name || relatedPost.categorySlug
										}
										categorySlug={relatedPost.categorySlug}
										categoryColor={relatedPost.category?.color}
										readingTimeMinutes={relatedPost.readingTimeMinutes}
										publishedAt={relatedPost.publishedAt}
									/>
								))}
							</div>
						</div>
					</section>
				)}

				{/* Explore StyTrix — SEO internal linking to tools, glossary, compare, use-cases */}
				<section className="border-border border-t bg-background px-6 py-12">
					<div className="mx-auto max-w-7xl space-y-10">
						{/* Tools */}
						<div>
							<h2 className="mb-6 text-center font-bold text-2xl text-foreground tracking-tight">
								{isZh ? '探索 StyTrix 工具' : 'Explore StyTrix Tools'}
							</h2>
							<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
								<Link
									href="/tools/ai-fashion-generator"
									className="group rounded-xl border border-border bg-card p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elevated"
								>
									<h3 className="font-semibold text-foreground text-sm transition-colors group-hover:text-primary">
										{isZh ? 'AI 時尚生成器' : 'AI Fashion Generator'}
									</h3>
									<p className="mt-1 text-muted-foreground text-xs">
										{isZh ? '文字轉時尚設計' : 'Text to fashion design'}
									</p>
								</Link>
								<Link
									href="/tools/virtual-fashion-sampling"
									className="group rounded-xl border border-border bg-card p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elevated"
								>
									<h3 className="font-semibold text-foreground text-sm transition-colors group-hover:text-primary">
										{isZh ? '虛擬打樣' : 'Virtual Sampling'}
									</h3>
									<p className="mt-1 text-muted-foreground text-xs">
										{isZh ? '取代實體樣品' : 'Replace physical samples'}
									</p>
								</Link>
								<Link
									href="/tools/real-time-collaboration"
									className="group rounded-xl border border-border bg-card p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elevated"
								>
									<h3 className="font-semibold text-foreground text-sm transition-colors group-hover:text-primary">
										{isZh ? '即時協作' : 'Real-Time Collaboration'}
									</h3>
									<p className="mt-1 text-muted-foreground text-xs">
										{isZh ? '即時共同設計' : 'Design together live'}
									</p>
								</Link>
							</div>
							<div className="mt-4 text-center">
								<Link
									href="/tools"
									className="link-underline text-primary text-sm transition-colors hover:text-primary/80"
								>
									{isZh ? '查看所有工具 →' : 'View all tools →'}
								</Link>
							</div>
						</div>

						{/* Use Cases */}
						<div>
							<h3 className="mb-4 text-center font-semibold text-foreground text-lg tracking-tight">
								{isZh ? 'AI 時尚應用場景' : 'AI Fashion Use Cases'}
							</h3>
							<div className="grid grid-cols-2 gap-3 md:grid-cols-5">
								<Link
									href="/use-cases/concept-creation"
									className="group rounded-lg border border-border bg-card p-3 transition-all duration-300 hover:border-primary/30 hover:shadow-sm"
								>
									<span className="font-medium text-foreground text-sm transition-colors group-hover:text-primary">
										{isZh ? '概念創作' : 'Concept Creation'}
									</span>
								</Link>
								<Link
									href="/use-cases/virtual-sampling"
									className="group rounded-lg border border-border bg-card p-3 transition-all duration-300 hover:border-primary/30 hover:shadow-sm"
								>
									<span className="font-medium text-foreground text-sm transition-colors group-hover:text-primary">
										{isZh ? '虛擬打樣' : 'Virtual Sampling'}
									</span>
								</Link>
								<Link
									href="/use-cases/collection-planning"
									className="group rounded-lg border border-border bg-card p-3 transition-all duration-300 hover:border-primary/30 hover:shadow-sm"
								>
									<span className="font-medium text-foreground text-sm transition-colors group-hover:text-primary">
										{isZh ? '系列規劃' : 'Collection Planning'}
									</span>
								</Link>
								<Link
									href="/use-cases/lookbook-generation"
									className="group rounded-lg border border-border bg-card p-3 transition-all duration-300 hover:border-primary/30 hover:shadow-sm"
								>
									<span className="font-medium text-foreground text-sm transition-colors group-hover:text-primary">
										{isZh ? 'Lookbook 生成' : 'Lookbook Generation'}
									</span>
								</Link>
								<Link
									href="/use-cases/brand-consistency"
									className="group rounded-lg border border-border bg-card p-3 transition-all duration-300 hover:border-primary/30 hover:shadow-sm"
								>
									<span className="font-medium text-foreground text-sm transition-colors group-hover:text-primary">
										{isZh ? '品牌一致性' : 'Brand Consistency'}
									</span>
								</Link>
							</div>
							<div className="mt-3 text-center">
								<Link
									href="/use-cases"
									className="link-underline text-primary text-sm transition-colors hover:text-primary/80"
								>
									{isZh ? '查看所有應用場景 →' : 'View all use cases →'}
								</Link>
							</div>
						</div>

						{/* Glossary + Compare */}
						<div className="grid gap-6 md:grid-cols-2">
							<div>
								<h3 className="mb-3 font-semibold text-foreground text-lg tracking-tight">
									{isZh ? 'AI 時尚詞彙表' : 'AI Fashion Glossary'}
								</h3>
								<div className="space-y-2">
									<Link
										href="/glossary/ai-fashion-design"
										className="block text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{isZh
											? '什麼是 AI 時尚設計？'
											: 'What is AI Fashion Design?'}
									</Link>
									<Link
										href="/glossary/lora-training"
										className="block text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{isZh ? '什麼是 LoRA 訓練？' : 'What is LoRA Training?'}
									</Link>
									<Link
										href="/glossary/virtual-fashion-sampling"
										className="block text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{isZh
											? '什麼是虛擬時尚打樣？'
											: 'What is Virtual Fashion Sampling?'}
									</Link>
									<Link
										href="/glossary/generative-ai"
										className="block text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{isZh ? '什麼是生成式 AI？' : 'What is Generative AI?'}
									</Link>
									<Link
										href="/glossary"
										className="link-underline mt-2 block text-primary text-sm transition-colors hover:text-primary/80"
									>
										{isZh ? '查看完整詞彙表 →' : 'View full glossary →'}
									</Link>
								</div>
							</div>
							<div>
								<h3 className="mb-3 font-semibold text-foreground text-lg tracking-tight">
									{isZh ? '比較 StyTrix' : 'Compare StyTrix'}
								</h3>
								<div className="space-y-2">
									<Link
										href="/compare/stytrix-vs-midjourney"
										className="block text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										StyTrix vs Midjourney
									</Link>
									<Link
										href="/compare/stytrix-vs-dalle"
										className="block text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										StyTrix vs DALL-E
									</Link>
									<Link
										href="/compare/stytrix-vs-clo3d"
										className="block text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										StyTrix vs CLO 3D
									</Link>
									<Link
										href="/compare/stytrix-vs-fashionai"
										className="block text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										StyTrix vs FashionAI
									</Link>
									<Link
										href="/compare"
										className="link-underline mt-2 block text-primary text-sm transition-colors hover:text-primary/80"
									>
										{isZh ? '查看所有比較 →' : 'View all comparisons →'}
									</Link>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* CTA Section */}
				<section className="bg-primary px-6 py-20">
					<div className="mx-auto max-w-4xl text-center">
						<h2 className="mb-6 font-bold text-3xl text-white tracking-tight md:text-4xl">
							{tCta('sectionTitle')}
						</h2>
						<p className="mb-8 text-lg text-white/90">
							{tCta('sectionSubtitle')}
						</p>
						{waitlistMode ? (
							<button
								onClick={() => setIsWaitlistOpen(true)}
								className="inline-flex items-center gap-2 rounded-lg bg-white px-8 py-4 font-semibold text-lg text-primary transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/95 hover:shadow-xl active:translate-y-0"
							>
								{tCta('floatingButtonWaitlist')}
							</button>
						) : (
							<a
								href="/sign-in"
								className="inline-flex items-center gap-2 rounded-lg bg-white px-8 py-4 font-semibold text-lg text-primary transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/95 hover:shadow-xl active:translate-y-0"
							>
								{tCta('floatingButton')}
							</a>
						)}
					</div>
				</section>
			</main>
			<SiteFooter />

			{/* Waitlist Modal */}
			<WaitlistModal
				isOpen={isWaitlistOpen}
				onClose={() => setIsWaitlistOpen(false)}
			/>
		</div>
	);
}
